/* TargetResultsViewComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const Renderer = require("./TargetResultsViewRenderer");
const _ = require("lodash");

//const DeviceNameColumn = require("./column_renderers/device_name/DeviceNameComponent");
const TimestampColumn = require("./column_renderers/timestamp/TimestampComponent");
const StatusColumn = require("./column_renderers/status/StatusComponent");
//const ErrorMessageColumn = require("./column_renderers/error_message/ErrorMessageComponent");

const PagingTargetResultsFeed = require("./PagingTargetResultsFeed");

class TargetResultsViewComponent extends EventEmitter {
    constructor(logger, gameEditor, state, storageFactory, solutionsManager, userSession) {
        super();

        this._logger = logger;
        this._gameEditor = gameEditor;
        this._triggerEntity = state.triggerEntity;
        this._workEntity = undefined;
        this._pagingFeed = undefined;
        this._renderer = new Renderer(this);
        this._userSession = userSession;

        this._storageFactory = storageFactory;
        this._solutionName = (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "futureView";
    }

    get columnConfig() {
        let columnConfig = {
            targetName: {
                label: "Name",
                //columnRendererComponent: DeviceNameColumn,
                width: 200
            },
            startTime: {
                label: "Start Time",
                columnRendererComponent: TimestampColumn,
                dataType: "date",
                updateFromEntity: true,
                width: 150,
                sort: "desc"
            },
            completeTime: {
                label: "Completion Time",
                columnRendererComponent: TimestampColumn,
                dataType: "date",
                updateFromEntity: true,
                width: 150
            },
            status: {
                label: "Target Status",
                columnRendererComponent: StatusColumn,
                width: 80
            },
            errorMsg: {
                path: "errorMsg",
                label: "Error Message",
                shown: true,
                //columnRendererComponent: ErrorMessageColumn,
                width: 820
            }
        };

        return columnConfig;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get pagingFeed() {
        return this._pagingFeed;
    }

    shutdown() {
        this._unregisterListeners();
        let shutdowns = [this._renderer.shutdown(), this._pagingFeed.shutdown(), this._workEntity.shutdown()];
        return Promise.all(shutdowns).then(
            () => {
                this._routeData = undefined;
                this._TriggerTypeEntityDef = undefined;
                this._userSession = undefined;
                this._gridKey = undefined;
                this._userGridData = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._steveUtils = undefined;
                this._explorerState = undefined;
                this._pagingFeed = undefined;
                this._logger = undefined;
            },
            (err) => {
                this._logger.warn({ error: err }, "WorkTrigGridViewComponent.shutdown() error");
            }
        );
    }

    whenModelIsReady() {
        return this._storageFactory
            .whenStorageIsReady()
            .then(() => {
                return this._triggerEntity.loadRelatedWork().then((result) => {
                    this._workEntity = result;
                    this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid("entity_grid_gl", this._gridKey);
                    const userSorting = {}; //this._userGridData.getSorting() || {};
                    this._pagingFeed = new PagingTargetResultsFeed({
                        feedKey: this._gridKey + "_feed",
                        userSession: this._userSession,
                        entityDef: this._workEntity,
                        columnConfig: this.columnConfig,
                        filter: {},
                        sort: userSorting,
                        additionalDataFields: []
                    });
                    return Promise.resolve();
                });
            })
            .then(() => {
                return this._pagingFeed.whenFeedReady();
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get gameEditor() {
        return this._gameEditor;
    }

    get explorerState() {
        return this._explorerState;
    }

    set explorerState(val) {
        this.setExplorerState(val);
    }

    setExplorerState(val) {
        this._explorerState = val;
    }

    updateExplorerState(property, val) {
        _.set(this._explorerState, property, val);
    }

    getTargetResultsRows() {
        if (!this._workEntity) {
            return {};
        } else {
            let rows = {};
            let targetResults = this._workEntity.targetResults;
            _.forEach(this._workEntity.targets, (target, targetId) => {
                let targetResult = _.get(targetResults, [targetId, 0], {});
                rows[targetId] = {
                    name: target.name,
                    status: targetResult.status,
                    startTime: targetResult.startTime,
                    completeTime: targetResult.completeTime,
                    errorMsg: targetResult.errorMsg
                };
            });
            return rows;
        }
    }

    getViewSettings() {}

    onUserSorted(sortData) {
        //revised algo that addresses path values when used in the columnconfig
        sortData = [].concat(sortData);
        let sort = {};

        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath || this.columnConfig[sortData.sortColumn].path || sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        this._patchSorting(sort);

        this._pagingFeed.setSort(sort);
    }

    _patchSorting(sorting) {
        // Adding displayName as secondary sorting criteria
        if (sorting && Object.keys(sorting).length === 1) {
            if (this.columnConfig.targetName) {
                if (!sorting.targetName) {
                    sorting.targetName = 1;
                    sorting.id = 1;
                } else {
                    sorting.id = 1;
                }
            }
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = TargetResultsViewComponent;

TargetResultsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.storageFactory",
    "$teve.solutionsManager",
    "$teve.userSession"
];

TargetResultsViewComponent.$displayDetails = {
    type: "View",
    name: "Target Results",
    icon: "common.edit",
    shown: false
};
